import { ReactElement, useCallback, useMemo } from 'react'
import {
  Box,
  Card,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { Visibility } from '@mui/icons-material'
import { useAppSelector } from 'src/store/hooks'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  selectAssignmentUserProgressV4,
  AssignmentDetailV3,
  CourseWithStats,
} from 'src/v3/manage/manage.slice'
import {
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'
import { dataGridSx } from '../../styles'
import { getProgressColor, getIsOverdue } from '../utils/helpers'

type CoursesProgressCardProps = {
  assignment: AssignmentDetailV3 | null
  isLoading: boolean
  onViewCourseProgress: (courseID: string) => void
}

function CoursesProgressCardSkeleton(): ReactElement {
  return (
    <Card className="p-6 mb-6">
      <Skeleton variant="text" width={180} height={28} className="mb-4" />
      <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
    </Card>
  )
}

export default function CoursesProgressCard({
  assignment,
  isLoading,
  onViewCourseProgress,
}: CoursesProgressCardProps): ReactElement {
  let courses = assignment?.courses || []
  let users = assignment?.users || []
  let userProgress = useAppSelector(selectAssignmentUserProgressV4)
  let isOverdue = getIsOverdue(assignment?.dueDate || '')

  let courseStatistics = useMemo(
    () =>
      courses.map((c: CourseWithStats) => {
        let totalAssignedUsers = users.length
        let usersCompletedCourse = userProgress.filter((user) =>
          user.courseProgress?.some(
            (cp) => cp.courseID === c.id && cp.status === 'Completed',
          ),
        ).length

        let completionPercentage =
          totalAssignedUsers > 0
            ? Math.round((usersCompletedCourse / totalAssignedUsers) * 100)
            : 0

        return {
          id: c.id,
          name: c.name,
          completedUsers: usersCompletedCourse,
          totalUsers: totalAssignedUsers,
          completionPercentage,
        }
      }),
    [courses, users, userProgress],
  )

  let gridColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'completionPercentage',
        headerName: 'Progress',
        width: 200,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value: number) => `${value}%`,
        renderCell: (params) => {
          let progressColor = getProgressColor(params.value, isOverdue)

          return (
            <Box
              className="flex flex-col items-center justify-center"
              sx={{ height: '100%', width: '100%' }}
            >
              <Box component="span" sx={{ color: progressColor }}>
                {params.value}%
              </Box>
              <LinearProgress
                variant="determinate"
                value={params.value}
                className="m-auto rounded-sm"
                sx={{
                  height: 6,
                  width: '100%',
                  maxWidth: 200,
                }}
                color={progressColor}
              />
            </Box>
          )
        },
      },
      {
        field: 'userBreakdown',
        headerName: 'User Breakdown',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: (params) => (
          <Box
            className="flex items-center justify-end"
            sx={{ height: '100%' }}
          >
            <Tooltip title="View User Progress for Course">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  onViewCourseProgress(params.row.id)
                }}
                color="primary"
              >
                <Visibility />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [isOverdue, onViewCourseProgress],
  )

  let CoursesToolbar = useCallback(
    () => (
      <Box sx={{ backgroundColor: 'background.paperDark' }}>
        <DataGridToolbar
          fileName="course-completion-rates"
          showQuickFilter={false}
        />
      </Box>
    ),
    [],
  )

  if (!assignment?.courses || isLoading) {
    return <CoursesProgressCardSkeleton />
  }

  return (
    <Card className="mb-6" sx={{ bgcolor: 'background.paper' }}>
      <Box className="p-6 pb-2">
        <Typography variant="h6" component="h2" className="mb-2">
          Course Completion Rates ({courses.length}{' '}
          {courses.length === 1 ? 'Course' : 'Courses'})
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-4">
          At-a-glance view of completion rates for courses in the assignment
        </Typography>
      </Box>
      {courses.length === 0 ? (
        <Box className="px-6 pb-6">
          <Typography color="text.secondary">
            No courses assigned to this assignment.
          </Typography>
        </Box>
      ) : (
        <Box className="px-6 pb-6">
          <DataGridPremium
            rows={courseStatistics}
            columns={gridColumns}
            slots={{ toolbar: CoursesToolbar }}
            showToolbar
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: DATAGRID_DEFAULT_PAGE_SIZE,
                },
              },
              density: 'standard',
              pinnedColumns: { right: ['userBreakdown'] },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            pagination
            disableRowSelectionOnClick
            sx={dataGridSx}
          />
        </Box>
      )}
    </Card>
  )
}
